import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../ui/Modal';
import Button from '../ui/Button';
import LineupVoteList from './LineupVoteList';
import LineupScheduleGrid from './LineupScheduleGrid';
import LineupViewToggle from './LineupViewToggle';
import {
  festivalDaysFromLineup,
  formatDayLabelLong,
  formatDayToggleParts,
  voteKey,
} from '../../utils/lineupFormat';
import { lineupHasScheduleData } from '../../utils/scheduleGrid';
import {
  useEventLineup,
  useMyArtistVotes,
  useMyEventAttendance,
} from '../../hooks/queries/useEventLineup';
import { saveArtistVotesMutationFn, saveEventAttendanceMutationFn } from '../../api/artistVotes';
import { queryKeys } from '../../lib/query/queryKeys';

const PREVIEW_NAME_COUNT = 3;

function previewNamesForDay(lineup, day) {
  const dayRows = lineup.filter((row) => row.performance_day === day);
  const headliners = dayRows.filter((row) => row.role === 'headliner');
  const pool = headliners.length > 0 ? headliners : dayRows;
  return pool.slice(0, PREVIEW_NAME_COUNT).map((row) => row.artist_name);
}

export default function FestivalMatchSetupModal({
  isOpen,
  onClose,
  eventId,
  eventName,
  userId,
  onComplete,
  mode = 'setup',
  startStep,
  focusDay = null,
}) {
  const queryClient = useQueryClient();
  const listRef = useRef(null);
  const { data: lineup = [], isLoading: lineupLoading } = useEventLineup(eventId, {
    enabled: isOpen,
  });
  const { data: savedDays = [] } = useMyEventAttendance(eventId, userId);
  const { data: savedVotes = [] } = useMyArtistVotes(eventId, userId);

  const festivalDays = useMemo(() => festivalDaysFromLineup(lineup), [lineup]);
  const isEditMode = mode === 'edit';

  const [step, setStep] = useState('days');
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedVotes, setSelectedVotes] = useState(() => new Set());
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [lineupView, setLineupView] = useState('list');

  useEffect(() => {
    if (!isOpen) return;
    const defaultStep = festivalDays.length > 0 ? 'days' : 'votes';
    setStep(startStep ?? defaultStep);
    setSelectedDays(savedDays.length > 0 ? savedDays : festivalDays);
    setSelectedVotes(new Set(savedVotes.map((v) => voteKey(v.artist_id, v.performance_day))));
    setCurrentDayIndex(0);
    setSaving(false);
    setShowCloseConfirm(false);
    setLineupView('list');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, startStep, festivalDays.length, savedDays.length, savedVotes.length]);

  useEffect(() => {
    if (!isOpen || step !== 'votes') return;
    if (focusDay) {
      const days = selectedDays.length > 0 ? selectedDays : festivalDays;
      const idx = days.indexOf(focusDay);
      setCurrentDayIndex(idx >= 0 ? idx : 0);
    } else {
      setCurrentDayIndex(0);
    }
  }, [isOpen, step, focusDay, selectedDays, festivalDays]);

  const toggleDay = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort(),
    );
  };

  const toggleVote = (artistId, performanceDay) => {
    const key = voteKey(artistId, performanceDay);
    setSelectedVotes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const saveAndComplete = async ({ skipVotes = false } = {}) => {
    if (!userId) return;
    setSaving(true);
    try {
      await saveEventAttendanceMutationFn({
        eventId,
        userId,
        attendingDays: selectedDays,
      });
      if (!skipVotes) {
        const selections = [];
        selectedVotes.forEach((key) => {
          const [artistId, day] = key.split('|');
          if (day && selectedDays.length > 0 && !selectedDays.includes(day)) return;
          selections.push({ artistId, performanceDay: day || null });
        });
        await saveArtistVotesMutationFn({ eventId, userId, selections });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.events.myAttendance(eventId, userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.events.myArtistVotes(eventId, userId) });
      await onComplete?.();
    } catch (err) {
      console.error('[FestivalMatchSetup] save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleNextDay = () => {
    setCurrentDayIndex((i) => i + 1);
    listRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
    const modalBody = listRef.current?.closest?.('.vote-modal-body');
    modalBody?.scrollTo?.({ top: 0, behavior: 'smooth' });
  };

  const requestClose = () => {
    if (step === 'votes' && !isEditMode) {
      setShowCloseConfirm(true);
      return;
    }
    onClose?.();
  };

  const confirmSkipAndClose = async () => {
    setShowCloseConfirm(false);
    await saveAndComplete({ skipVotes: true });
    onClose?.();
  };

  if (!isOpen) return null;

  if (step === 'days') {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="sm" title="Which days are you going?">
        <ModalBody className="day-modal">
          <p className="mb-3 lineup-desc">
            {eventName
              ? `Match with people going to ${eventName} the same days.`
              : 'Match with people going the same days.'}
          </p>
          <div className="space-y-1.5">
            {festivalDays.map((day) => {
              const selected = selectedDays.includes(day);
              const preview = previewNamesForDay(lineup, day);
              const { name, date } = formatDayToggleParts(day);
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleDay(day)}
                  aria-pressed={selected}
                  className={`day-toggle-row ${selected ? 'selected' : 'unselected'}`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="day-toggle-row__name">{name}</span>
                    <span className="day-toggle-row__date block">{date}</span>
                    {preview.length > 0 && (
                      <span className="day-toggle-row__preview block truncate">
                        {preview.join(' · ')}
                      </span>
                    )}
                  </span>
                  <span
                    className={`day-toggle-row__check border ${
                      selected
                        ? 'border-[var(--glass-accent-ring)] bg-[image:var(--glass-accent-fill)] text-ink'
                        : 'border-[var(--glass-neutral-ring)] bg-transparent'
                    }`}
                    aria-hidden
                  >
                    {selected && <Check size={13} strokeWidth={2.4} />}
                  </span>
                </button>
              );
            })}
          </div>
        </ModalBody>
        <ModalFooter align="stack">
          <Button fullWidth disabled={selectedDays.length === 0} onClick={() => setStep('votes')}>
            Next: pick your artists
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  const votingDays = focusDay ? [focusDay] : selectedDays.length > 0 ? selectedDays : festivalDays;
  const currentDay = votingDays[currentDayIndex] ?? votingDays[0];
  const isLastDay = currentDayIndex >= votingDays.length - 1;
  const dayLabel = `Day ${currentDayIndex + 1} of ${votingDays.length}`;

  const pickedCount = [...selectedVotes].filter((key) => {
    const day = key.split('|')[1];
    if (!day) return true;
    return votingDays.includes(day);
  }).length;

  const showFloatingBar = pickedCount >= 1;
  const bodyPadClass = showFloatingBar ? 'vote-modal-body--with-bar' : '';
  const canShowSchedule = lineupHasScheduleData(lineup, currentDay);
  const activeLineupView = canShowSchedule ? lineupView : 'list';

  return (
    <>
      <Modal isOpen={isOpen} onClose={requestClose} size="md" showClose={false}>
        <ModalHeader onClose={requestClose}>
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-lg font-semibold leading-snug text-ink">Who do you want to see?</h2>
            <span className="picked-pill shrink-0">{pickedCount} picked</span>
          </div>
        </ModalHeader>

        {currentDay && (
          <div className="vote-day-banner">
            <span>{formatDayLabelLong(currentDay)}</span>
            {votingDays.length > 1 && !focusDay && (
              <span className="vote-day-banner__progress">{dayLabel}</span>
            )}
          </div>
        )}

        {canShowSchedule && (
          <LineupViewToggle
            view={activeLineupView}
            onChange={setLineupView}
            className="!mx-4 !mb-2"
          />
        )}

        <ModalBody className={`vote-modal-body max-h-[50vh] overflow-y-auto ${bodyPadClass}`}>
          {lineupLoading ? (
            <p className="py-6 text-center text-sm text-ink/60">Loading lineup…</p>
          ) : activeLineupView === 'schedule' ? (
            <LineupScheduleGrid
              lineup={lineup}
              day={currentDay}
              selectedKeys={selectedVotes}
              onToggle={toggleVote}
            />
          ) : (
            <LineupVoteList
              listRef={listRef}
              lineup={lineup}
              selectedKeys={selectedVotes}
              onToggle={toggleVote}
              singleDay={currentDay}
            />
          )}
        </ModalBody>

        <ModalFooter align="stack" className="vote-modal-footer !px-4 !pt-2 !pb-5">
          {showFloatingBar ? (
            <div className="vote-progress-bar">
              <span className="vote-progress-bar__label">{dayLabel}</span>
              {isLastDay ? (
                <Button
                  className="vote-progress-bar__btn"
                  loading={saving}
                  onClick={() => saveAndComplete()}
                >
                  Save
                </Button>
              ) : (
                <Button className="vote-progress-bar__btn" onClick={handleNextDay}>
                  Next
                </Button>
              )}
            </div>
          ) : (
            <>
              {isLastDay ? (
                <Button fullWidth loading={saving} onClick={() => saveAndComplete()}>
                  Save
                </Button>
              ) : (
                <Button fullWidth onClick={handleNextDay}>
                  Next
                </Button>
              )}
              {!isEditMode && (
                <Button
                  variant="ghost"
                  fullWidth
                  disabled={saving}
                  onClick={() => saveAndComplete({ skipVotes: true })}
                  className="!h-auto justify-center py-2.5 font-normal underline underline-offset-2"
                >
                  Skip voting for now
                </Button>
              )}
            </>
          )}
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        title="Close without voting?"
        size="sm"
      >
        <ModalBody>
          <p className="lineup-desc">Leaving without voting will prevent you from being matched.</p>
        </ModalBody>
        <ModalFooter align="split">
          <Button variant="secondary" onClick={() => setShowCloseConfirm(false)}>
            Keep voting
          </Button>
          <Button loading={saving} onClick={confirmSkipAndClose}>
            Skip voting
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
